import { Fragment, useMemo } from 'react';
import { differenceInCalendarDays } from 'date-fns';
import { toCalendarDateTime } from '@internationalized/date';
import { MdOutlineKeyboardArrowRight } from 'react-icons/md';
import { Tooltip } from '@ds';
import { AirportListItemType } from '@lp/features/RoutePlanner/types';
import {
  getFlightDateTime,
  getAirportInfo,
  getCommercialTime,
  getAbsoluteCommercialDate,
  getCommercialZonedDateTime,
} from '@lp/features/RoutePlanner/components/RouteLegForm/components/SearchRecommendedFlights/utils';
import { RecommendedFlightType } from '@/api/apis/route';
import { convertMsToTimeString, getTimeDifference } from '@/utils/helpers';

type CommercialFlightInfoProps = {
  departureAirport: AirportListItemType | null;
  arrivalAirport: AirportListItemType | null;
  flight: RecommendedFlightType;
};

export const FlightStopsTooltip = ({ flight }: Pick<CommercialFlightInfoProps, 'flight'>) => {
  return (
    <div className="w-full flex flex-col gap-4 px-2 py-1">
      {flight.legs.map((leg, index) => {
        const departureDateTime = getCommercialZonedDateTime(leg.departureTimeLocal);
        const arrivalDateTime = getCommercialZonedDateTime(leg.arrivalTimeLocal);
        const departureTime = getCommercialTime(departureDateTime);
        const arrivalTime = getCommercialTime(arrivalDateTime);
        const prevArrivalTime = flight.legs[index - 1]
          ? getCommercialZonedDateTime(flight.legs[index - 1].arrivalTimeLocal)
          : null;
        const legDuration = getTimeDifference(
          new Date(departureDateTime.toAbsoluteString()),
          new Date(arrivalDateTime.toAbsoluteString()),
        );
        const stopDuration = prevArrivalTime
          ? getTimeDifference(
              new Date(departureDateTime.toAbsoluteString()),
              new Date(prevArrivalTime.toAbsoluteString()),
            )
          : null;
        const flightInfo = [leg.flightOperator, leg.flightNumber, leg.seatType].filter((i) => i).join(' | ');

        return (
          <Fragment key={leg.departureTime}>
            {!!stopDuration && (
              <span className="text-m-sb text-black">{`1 stop ${convertMsToTimeString(stopDuration.MILLISECONDS)} in ${leg.departureAirportIATA}`}</span>
            )}
            <div className="flex flex-col">
              <span className="text-m-sb text-black">{convertMsToTimeString(legDuration.MILLISECONDS)}</span>
              <span className="text-m-r text-black">{`${departureTime} - ${leg.departureAirportIATA}`}</span>
              <span className="text-m-r text-black">{`${arrivalTime} - ${leg.arrivalAirportIATA}`}</span>
              {!!flightInfo && <span className="text-m-r text-black">{flightInfo}</span>}
            </div>
          </Fragment>
        );
      })}
    </div>
  );
};

export const CommercialFlightInfo = ({ departureAirport, arrivalAirport, flight }: CommercialFlightInfoProps) => {
  const flightTimes = getFlightDateTime(flight.legs);
  const startAirportInfo = getAirportInfo(departureAirport, flightTimes.departureDateTime);
  const endAirportInfo = getAirportInfo(arrivalAirport, flightTimes.arrivalDateTime);
  const departureDateTime = getAbsoluteCommercialDate(flightTimes.departureDateTime);
  const arrivalDateTime = getAbsoluteCommercialDate(flightTimes.arrivalDateTime);

  const flightDuration = useMemo(() => {
    const timeDifference = getTimeDifference(
      new Date(departureDateTime.toAbsoluteString()),
      new Date(arrivalDateTime.toAbsoluteString()),
    );
    return convertMsToTimeString(timeDifference.MILLISECONDS, 'short');
  }, [departureDateTime, arrivalDateTime]);

  const arrivalDaysDiff = useMemo(() => {
    const daysDiff = Math.abs(
      differenceInCalendarDays(
        toCalendarDateTime(arrivalDateTime).toString(),
        toCalendarDateTime(departureDateTime).toString(),
      ),
    );

    return daysDiff ? ` (+${daysDiff} ${daysDiff > 1 ? 'days' : 'day'})` : '';
  }, [departureDateTime, arrivalDateTime]);

  const stopsText = useMemo(() => {
    if (flight.legs.length < 2) return '';

    const stops = flight.legs.length - 1;

    return `${stops} ${stops > 1 ? 'stops' : 'stop'}`;
  }, [flight.legs.length]);

  return (
    <div className="flex items-center px-3">
      <div className="flex flex-col items-center w-12">
        <span className="text-s-sb text-base-5">{startAirportInfo.code}</span>
        <span className="text-s-sb text-base-20">{startAirportInfo.time}</span>
      </div>
      <div className="flex flex-col items-center justify-end px-1 w-28">
        {!!stopsText && (
          <Tooltip content={<FlightStopsTooltip flight={flight} />}>
            <span className="px-1 py-0.5 text-xs-m text-white flex justify-center items-center bg-black rounded-full">
              {stopsText}
            </span>
          </Tooltip>
        )}
        <div className="w-full flex items-center text-base-20">
          <div className="w-full border-b-1 border-dashed border-base-20" />
          <div className="-ml-1">
            <MdOutlineKeyboardArrowRight />
          </div>
        </div>
        <span className="text-s-sb text-base-20">{flightDuration}</span>
      </div>
      <div className="flex flex-col items-center">
        <span className="text-s-sb text-base-5">{endAirportInfo.code}</span>
        <span className="text-s-sb text-base-20">{`${endAirportInfo.time}${arrivalDaysDiff}`}</span>
      </div>
    </div>
  );
};
